/*
 * Created on Sat Apr 24 2021
 *
 * Copyright (c) 2021 Khor Chai Hong
 */
import React from 'react';
import "bootstrap/dist/css/bootstrap.css";
import './App.scss';
import './assets/styles/editor.scss';
import { useState, useEffect, useRef, Suspense, useMemo } from 'react';
// simport lazy from "react-lazy-with-preload";
import { Switch, Route, Redirect, useLocation, useRouteMatch, useParams } from "react-router-dom";
import Loader from "./pages/loader";
import { useWindowDimensions, useUserStatus } from './utils/helpers';
import ErrorBoundary from './Error';
import Snap from './pages/snap'

import Zine from './pages/zine';
import Login from './pages/login';
import CollectivesPage from 'pages/collectives';
import Search from './pages/search/search';
import Privacy from './pages/privacy';
import Terms from './pages/terms';
import ContactUs from 'pages/contactus';
import SignUp from 'pages/signup'
import Find from 'pages/find'
import ResetPassword from 'pages/resetpassword';
import HowItWorks from 'pages/howitworks'
import Faq from './pages/faq';
import ConfirmChangePassword from './pages/confirm-change-password'
import ConfirmChangeEmail from './pages/confirm-change-email'
import ConfirmDeactivation from './pages/confirm-deactivation'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ModalProvider } from './contexts/modal.context';
import ModalCollections from './components/modals.collection';

import { ErrorAlertProvider } from "./contexts/error-alert.context";
import { ActionAlertProvider } from "./contexts/action-alert.context";
import { NotifyAlertProvider } from "./contexts/notify-alert.context";
import { UserThemeProvider } from "./contexts/user-theme.context";
import { FullScreenProvider } from './contexts/fullscreen.context';
import { ActivitiesProvider } from 'contexts/activities.context';
import { PsstDrawerProvider } from './components/shared/psst/psst.drawer.context';
import { FollowingFollowersPopUpProvider } from './components/collective-item/following'
import { ActivityViewsProvider } from './contexts/activity.views.context'
import { FollowedByMeProvider } from './contexts/followed-by-me.context'
import { ActionsProvider } from './contexts/actions.context'
import { TasksProvider } from 'contexts/tasks.context';
import { TopNavThemeProvider } from 'components/shared/top-nav/top-nav-theme-context';
import { FeaturesProvider } from 'contexts/features.context'
import { PsstProvider } from 'contexts/psst.context';
import FeaturedArtworkThemeProvider from 'components/zine/featured-artwork.context'
import { DrawersProvider } from 'contexts/drawers.context';

import ErrorAlert from './components/shared/error-alert';
import ActionAlert from './components/shared/action-alert';
import NotifyAlert from './components/shared/notify-alert';

import { ToastProvider } from "./contexts/toast.context";
import { AuthProvider } from './contexts/auth.context';
import { ToastPopProvider } from './contexts/toast-pop.context'

import FullScreen from './components/gallery/fullscreen';

import SoloArtwork from './pages/solo-artwork';
import SoloArticle from 'pages/solo-article';

import Profile from './pages/profile';

import Manifesto from './pages/manifesto'

import TopNav from 'components/shared/top-nav/top-nav';

import useGetUserUrl from 'hooks/get-user-url.hook';
import ToastPop from 'components/shared/toast-pop/toast-pop';

import SoloItchArticle from './pages/solo-itch-article'
import PreviewFeaturedArtwork from './pages/preview-featured-artwork'
import PreviousItchArticles from './pages/previous-itch-articles'

import Ping2 from 'pages/ping2';

import { PageReadyProvider } from 'contexts/page-ready.context';

import VotePrompt from 'components/vote-prompt/vote-prompt';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: true // this will reload the past queries when the component gets mounted
    },
  },
});

const UserItchUrlRedirect = ({ children, component, ...rest }) => {
  const { id } = useParams()
  const { isSuccess, itchUrl } = useGetUserUrl(id)
  return (
    <>
    {isSuccess ? <Redirect to={{
        pathname: `/${itchUrl}`
      }}/> : null}
    </>
  )
}

const ProtectedRoute = ({ children, component, ...rest }) => {
  const user = useUserStatus()
  return (
    <Route
      {...rest}
      render={(location) => {
        return user ? 
          (children || React.createElement(component))
          : <Redirect to={{
              pathname: "/signup"
            }}
            />
      }}
    />
  )
}

const PublicRoute = ({ children, component, ...rest }) => {
  const user = useUserStatus()
  return (
    <Route
      {...rest}
      render={(location) => {
        return !user ? 
          (children || React.createElement(component))
          : <Redirect to={{
              pathname: "/home"
            }}
            />
      }}
    />
  )
}

function App() {
  // eslint-disable-next-line
  const { windowHeight, isMobile } = useWindowDimensions();
  const user = useUserStatus();
  const appRef = useRef();
  const location = useLocation();
  const path = location.pathname;

  useEffect(() => {
    window.history.scrollRestoration = 'manual'
    
    window.fbAsyncInit = function() {
      window.FB.init({
        appId            : process.env.REACT_APP_FFBAPPID,
        xfbml            : false,
        version          : 'v18.0'
      });
    };

    (function(d, s, id) {
      let js, fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) return;
      js = d.createElement(s); js.id = id;
      js.src = "https://connect.facebook.net/en_US/sdk.js";
      fjs.parentNode.insertBefore(js, fjs);
    }(document, 'script', 'facebook-jssdk'));

  }, []);

  const layoutHeight = useMemo(() => {
    return window.innerHeight
  }, [])
 
  useEffect(() => {
    // do not scroll up if at user profile page
    if (path.indexOf('/my-profile') === -1 && path.split('/').pop() !== 'gallery' && path.split('/').pop() !== 'articles') {
      window.scrollTo(0, 0);
    }
    
    window.gtag('event', 'page_view', {
      page_title: path,
      page_location: path
    });
  }, [path])

  return (
    <div ref={appRef} className="App" id="app" style={isMobile ?
      {
        position: 'relative',
        minHeight: layoutHeight
      } : null
    }>
      <ErrorBoundary>
        <Suspense fallback={<Loader />}>
          <PageReadyProvider>
            <FeaturesProvider>
              <AuthProvider>
                <QueryClientProvider client={queryClient}>
                  <FollowedByMeProvider>
                    <ActivityViewsProvider>
                      <UserThemeProvider>
                        <PsstProvider>
                          <ActivitiesProvider>
                            <ActionsProvider>
                              <ToastPopProvider>
                                <ToastProvider>
                                  <TasksProvider>
                                    <ActionAlertProvider>
                                      <NotifyAlertProvider>
                                        <FullScreenProvider>
                                          <PsstDrawerProvider>
                                            <ModalProvider>
                                              <ErrorAlertProvider>    
                                                <FollowingFollowersPopUpProvider>
                                                  <FeaturedArtworkThemeProvider>
                                                    <DrawersProvider>
                                                      <TopNavThemeProvider>
                                                        <ToastPop/>
                                                        <TopNav/>
                                                        {user && path !== '/home/tour' ? <VotePrompt/> : null}
                                                        <Switch location={location}>
                                                          <Route path="/" exact>
                                                            <Zine/>
                                                          </Route>
                                                          <Route path="/find">
                                                            <Redirect to="/home"/>
                                                          </Route>
                                                          <ProtectedRoute path="/my-profile" component={Profile} />
                                                          <Route path="/user/:id">
                                                            <UserItchUrlRedirect/>
                                                          </Route>
                                                          <PublicRoute path="/signup/:code?" component={SignUp} exact />
                                                          <Route path="/unsubscribe/:code">
                                                            <SignUp/>
                                                          </Route>
                                                          <ProtectedRoute path="/home/:accountType?/:subType?">
                                                            <Find />
                                                          </ProtectedRoute>
                                                          <PublicRoute path="/login">
                                                            {user ? <Redirect to="/home" /> : <Login />}
                                                          </PublicRoute>
                                                          <Route path="/confirm-change-password/:code"
                                                            component={ConfirmChangePassword}
                                                          />
                                                          <Route path="/confirm-change-email/:code"
                                                            component={ConfirmChangeEmail}
                                                          />
                                                          <Route path="/confirm-deactivation/:code"
                                                            component={ConfirmDeactivation}
                                                          />
                                                          <Route path="/reset-password/:code" component={ResetPassword} />
                                                          <Route
                                                            path="/here"
                                                            render={({ match: { path } }) => (
                                                              <>
                                                                <Route path={`${path}/privacy`}>
                                                                  <Privacy />
                                                                </Route>
                                                                <Route path={`${path}/terms`}>
                                                                  <Terms />
                                                                </Route>
                                                                <Route path={`${path}/contact-us`}>
                                                                  <ContactUs />
                                                                </Route>
                                                                <Route path={`${path}/manifesto`}>
                                                                  <Manifesto />
                                                                </Route>
                                                                <Route path={`${path}/how-it-works`}>
                                                                  <HowItWorks />
                                                                </Route>
                                                              </>
                                                            )}
                                                          />
                                                          <ProtectedRoute path="/search">
                                                            <Search/>
                                                          </ProtectedRoute>
                                                          <ProtectedRoute path="/collectives/:accountType?/:subType?">
                                                            <CollectivesPage/>
                                                          </ProtectedRoute>
                                                          <Route path="/zine/:chapter?">
                                                            <Zine/>
                                                          </Route>
                                                          <Route path="/previews/zine/:artworkId">
                                                            <PreviewFeaturedArtwork/>
                                                          </Route>
                                                          <Route path="/faq" component={Faq} />
                                                          
                                                          <Route path="/artworks/:id">
                                                            <SoloArtwork/>
                                                          </Route>
                                                          
                                                          <Route path="/articles/:slug">
                                                            <SoloArticle/>
                                                          </Route>
                                                          
                                                          <Route path="/itch-articles/:slug">
                                                            <SoloItchArticle/>
                                                          </Route>
                                                          
                                                          <Route path="/archives/itch-articles">
                                                            <PreviousItchArticles/>
                                                          </Route>
                                                          
                                                          <Route path="/ping2">
                                                            <Ping2/>
                                                          </Route>

                                                          <Route path="/snap" component={Snap} />
                                                          <Route path="/:itchUrl" component={Profile} />

                                                          <Route component={Snap} />
                                                          
                                                        </Switch>
                                                        <ErrorAlert />
                                                        <ActionAlert />
                                                        <NotifyAlert />
                                                      </TopNavThemeProvider>
                                                    </DrawersProvider>
                                                  </FeaturedArtworkThemeProvider>
                                                </FollowingFollowersPopUpProvider>
                                                <FullScreen />
                                                <ModalCollections />
                                              </ErrorAlertProvider>
                                            </ModalProvider>
                                          </PsstDrawerProvider>
                                        </FullScreenProvider>
                                      </NotifyAlertProvider>
                                    </ActionAlertProvider>
                                  </TasksProvider>
                                </ToastProvider>
                              </ToastPopProvider>
                            </ActionsProvider>
                          </ActivitiesProvider>
                        </PsstProvider>
                      </UserThemeProvider>
                    </ActivityViewsProvider>
                  </FollowedByMeProvider>
                </QueryClientProvider>
              </AuthProvider>
            </FeaturesProvider>
          </PageReadyProvider>
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}

export default App;
